<template>
  <div class="opening-hours">
    <!-- Banner Section -->
    <Banner
      image="https://images.unsplash.com/photo-1599467556385-48b57868f038?q=80&w=2070"
      title="Opening Hours"
      height="700px"
    />

    <!-- Wavy Divider -->
    <div class="wave-container">
      <svg class="wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <path class="wave-path" d="M0,96L40,106.7C80,117,160,139,240,154.7C320,171,400,181,480,165.3C560,149,640,107,720,90.7C800,75,880,85,960,112C1040,139,1120,181,1200,181.3C1280,181,1360,139,1400,117.3L1440,96L1440,400L1400,400C1360,400,1280,400,1200,400C1120,400,1040,400,960,400C880,400,800,400,720,400C640,400,560,400,480,400C400,400,320,400,240,400C160,400,80,400,40,400L0,400Z"></path>
      </svg>
    </div>

    <div class="container">
      <!-- Current Status Section -->
      <div class="status-card" :class="{ 'open': isCurrentlyOpen }">
        <div class="status-icon">
          <i :class="isCurrentlyOpen ? 'fas fa-door-open' : 'fas fa-door-closed'"></i>
        </div>
        <div class="status-content">
          <h2>{{ isCurrentlyOpen ? 'We\'re Open!' : 'Currently Closed' }}</h2>
          <p v-if="isCurrentlyOpen">We're open until {{ getTodayClosingTime }}</p>
          <p v-else>Opens {{ getNextOpeningTime }}</p>
        </div>
      </div>

      <!-- Regular Hours Section -->
      <div class="hours-section">
        <h3>Regular Opening Hours</h3>
        <div class="hours-grid">
          <div v-for="(schedule, day) in regularHours" :key="day" 
               class="day-card" :class="{ 'current-day': isCurrentDay(day) }">
            <div class="day-name">{{ day }}</div>
            <div class="time-slots">
              <template v-if="schedule.isOpen">
                <div class="time-slot">{{ schedule.hours }}</div>
              </template>
              <template v-else>
                <div class="closed">Closed</div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Holiday Hours Section -->
      <div class="holiday-section">
        <h3>Holiday Schedule</h3>
        <div class="holiday-grid">
          <div v-for="(schedule, index) in holidaySchedule" :key="index" class="holiday-card">
            <div class="holiday-icon">
              <i :class="schedule.icon"></i>
            </div>
            <div class="holiday-details">
              <h4>{{ schedule.holiday }}</h4>
              <p class="holiday-date">{{ schedule.date }}</p>
              <p class="holiday-hours">{{ schedule.hours || 'Closed' }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Important Notes -->
      <div class="notes-section">
        <h3>Important Notes</h3>
        <ul>
          <li>
            <i class="fas fa-info-circle"></i>
            Last entry is 2 hours before closing time
          </li>
          <li>
            <i class="fas fa-calendar-alt"></i>
            Hours may vary during Ramadan and public holidays
          </li>
          <li>
            <i class="fas fa-exclamation-triangle"></i>
            The park may close during severe weather conditions
          </li>
          <li>
            <i class="fas fa-clock"></i>
            Some attractions may close 30 minutes before park closing time
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Banner from "@/components/Banner.vue";

const regularHours = ref({
  'Sunday': { isOpen: true, hours: '11:00 AM - 7:00 PM' },
  'Monday': { isOpen: false, hours: null },
  'Tuesday': { isOpen: false, hours: null },
  'Wednesday': { isOpen: true, hours: '11:00 AM - 7:00 PM' },
  'Thursday': { isOpen: true, hours: '11:00 AM - 7:00 PM' },
  'Friday': { isOpen: true, hours: '11:00 AM - 7:00 PM' },
  'Saturday': { isOpen: true, hours: '11:00 AM - 7:00 PM' }
});

const holidaySchedule = ref([
  {
    holiday: 'Eid Al-Fitr',
    date: 'April 10-13, 2024',
    hours: '10:00 AM - 9:00 PM',
    icon: 'fas fa-moon'
  },
  {
    holiday: 'Eid Al-Adha',
    date: 'June 17-20, 2024',
    hours: '10:00 AM - 9:00 PM',
    icon: 'fas fa-star'
  },
  {
    holiday: 'National Day',
    date: 'September 23, 2024',
    hours: '10:00 AM - 10:00 PM',
    icon: 'fas fa-flag'
  },
  {
    holiday: 'New Year\'s Day',
    date: 'January 1, 2024',
    hours: null,
    icon: 'fas fa-calendar'
  }
]);

const isCurrentlyOpen = computed(() => {
  const now = new Date();
  const day = now.toLocaleDateString('en-US', { weekday: 'long' });
  const currentTime = now.getHours() * 100 + now.getMinutes();
  
  if (!regularHours.value[day].isOpen) return false;
  
  const [openTime, closeTime] = regularHours.value[day].hours.split(' - ')
    .map(time => {
      const [hours, minutes] = time.match(/(\d+):(\d+)/).slice(1);
      const period = time.includes('PM') ? 1200 : 0;
      return parseInt(hours) * 100 + parseInt(minutes) + period;
    });

  return currentTime >= openTime && currentTime < closeTime;
});

const getTodayClosingTime = computed(() => {
  const day = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  return regularHours.value[day].hours.split(' - ')[1];
});

const getNextOpeningTime = computed(() => {
  const now = new Date();
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
  
  // Find the next open day
  let daysChecked = 0;
  let nextDay = currentDay;
  
  while (daysChecked < 7) {
    if (regularHours.value[nextDay].isOpen) {
      return `${nextDay} at ${regularHours.value[nextDay].hours.split(' - ')[0]}`;
    }
    now.setDate(now.getDate() + 1);
    nextDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    daysChecked++;
  }
  
  return 'Soon';
});

const isCurrentDay = (day) => {
  const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  return day === currentDay;
};
</script>

<style scoped>
.opening-hours {
  position: relative;
  background: white;
  margin-top: 700px;
}

.wave-container {
  position: absolute;
  top: -200px;
  left: 0;
  width: 100%;
  height: 220px;
  overflow: hidden;
}

.wave-top {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: bottom;
  animation: waveFloat 5s ease-in-out infinite alternate;
}

.wave-path {
  fill: #ffffff;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.1));
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Status Card */
.status-card {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  margin-bottom: 40px;
  border-left: 5px solid #dc3545;
  transition: all 0.3s ease;
}

.status-card.open {
  border-left-color: #28a745;
}

.status-icon {
  font-size: 2.5rem;
  margin-right: 20px;
  color: #dc3545;
}

.status-card.open .status-icon {
  color: #28a745;
}

.status-content h2 {
  margin: 0;
  color: #333;
  font-size: 1.8rem;
}

.status-content p {
  margin: 5px 0 0;
  color: #666;
}

/* Hours Grid */
.hours-section {
  margin-bottom: 40px;
}

.hours-section h3 {
  color: #1268B1;
  margin-bottom: 20px;
  font-size: 1.5rem;
}

.hours-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.day-card {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
}

.day-card:hover {
  transform: translateY(-5px);
}

.current-day {
  border: 2px solid #1268B1;
}

.day-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}

.time-slots {
  color: #666;
}

.closed {
  color: #dc3545;
  font-style: italic;
}

/* Holiday Schedule */
.holiday-section {
  margin-bottom: 40px;
}

.holiday-section h3 {
  color: #1268B1;
  margin-bottom: 20px;
  font-size: 1.5rem;
}

.holiday-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.holiday-card {
  display: flex;
  align-items: center;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.holiday-icon {
  width: 50px;
  height: 50px;
  background: #1268B1;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 15px;
}

.holiday-details h4 {
  margin: 0;
  color: #333;
}

.holiday-date {
  color: #666;
  font-size: 0.9rem;
  margin: 5px 0;
}

.holiday-hours {
  color: #1268B1;
  font-weight: bold;
  margin: 0;
}

/* Notes Section */
.notes-section {
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
}

.notes-section h3 {
  color: #1268B1;
  margin-bottom: 20px;
  font-size: 1.5rem;
}

.notes-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.notes-section li {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  color: #666;
}

.notes-section li i {
  color: #1268B1;
  margin-right: 10px;
  font-size: 1.2rem;
}

@keyframes waveFloat {
  0% {
    transform: translateY(0) scaleY(1.02);
  }
  100% {
    transform: translateY(-10px) scaleY(1.05);
  }
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .container {
    padding: 20px;
  }

  .status-card {
    padding: 20px;
  }

  .wave-container {
    top: -150px;
    height: 170px;
  }
}

@media (max-width: 480px) {
  .wave-container {
    top: -100px;
    height: 120px;
  }
}

/* RTL Support */
:dir(rtl) .wave-top {
  transform: scaleX(-1);
}

:dir(rtl) .status-card {
  border-left: none;
  border-right: 5px solid #dc3545;
}

:dir(rtl) .status-card.open {
  border-right-color: #28a745;
}

:dir(rtl) .status-icon {
  margin-right: 0;
  margin-left: 20px;
}

:dir(rtl) .holiday-icon {
  margin-right: 0;
  margin-left: 15px;
}

:dir(rtl) .notes-section li i {
  margin-right: 0;
  margin-left: 10px;
}
</style>